// src/context/BranchContext.tsx
import React, { createContext, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { supabase } from '../utils/supabaseClient';
import { DietaryPreference } from '../types/dietary';
import { Currency } from '../types/currency';
import { getCurrenciesByBranchId } from '../services/currencyService';

interface BranchContextType {
  branchId: string | null;
  dietaryPreferences: DietaryPreference[];
  isLoadingPreferences: boolean;
  hasDietaryChanges: boolean;
  refreshPreferences: () => Promise<void>;
  addDietaryPreference: (data: Pick<DietaryPreference, 'name' | 'description' | 'ar'>) => Promise<{ success: boolean; error?: string }>;
  updateDietaryPreference: (id: string, data: Pick<DietaryPreference, 'name' | 'description' | 'ar'>) => Promise<{ success: boolean; error?: string }>;
  deleteDietaryPreference: (id: string) => Promise<{ success: boolean; error?: string }>;
  clearDietaryChanges: () => void;
  // Currencies
  currencies: Currency[];
  selectedCurrency: Currency | null;
  isLoadingCurrencies: boolean;
  refreshCurrencies: () => Promise<void>;
  setSelectedCurrency: (currencyId: string) => void;
}

const BranchContext = createContext<BranchContextType | undefined>(undefined);

interface BranchProviderProps {
  children: ReactNode;
  branchId: string | null;
}

export const BranchProvider: React.FC<BranchProviderProps> = ({ children, branchId }) => {
  const [dietaryPreferences, setDietaryPreferences] = useState<DietaryPreference[]>([]);
  const [isLoadingPreferences, setIsLoadingPreferences] = useState(false);
  const [hasDietaryChanges, setHasDietaryChanges] = useState(false);
  // Currencies state
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const [selectedCurrency, setSelectedCurrencyState] = useState<Currency | null>(null);
  const [isLoadingCurrencies, setIsLoadingCurrencies] = useState(false);
  //to avoid duplicate fetching
  const fetchingCurrenciesRef = useRef(false);
  const lastFetchedBranchIdRef = useRef<string | null>(null);


  const fetchDietaryPreferences = async () => {
    if (!branchId) {
      setDietaryPreferences([]);
      return;
    }

    setIsLoadingPreferences(true);
    try {
      const { data, error } = await supabase.rpc('get_all_preferences' as any, {
        branch_id: branchId
      });
      if (error) throw error;
      let preferencesArray = data;
      if (data && typeof data === 'object' && !Array.isArray(data)) {
        preferencesArray = data.preferences || data.data || data.items || [];
      }
      const mappedData: DietaryPreference[] = (Array.isArray(preferencesArray) ? preferencesArray : []).map((item: any) => ({
        id: item.id,
        name: item.name,
        description: item.description,
        ar: item.ar || { name: "", description: "" },
        branchId: branchId,
      }));
      setDietaryPreferences(mappedData);
    } catch (error) {
      setDietaryPreferences([]);
    } finally {
      setIsLoadingPreferences(false);
    }
  };
  const refreshPreferences = async () => {
    await fetchDietaryPreferences();
  };

  const addDietaryPreference = async (data: Pick<DietaryPreference, 'name' | 'description' | 'ar'>): Promise<{ success: boolean; error?: string }> => {
    if (!branchId) {
      return { success: false, error: 'No branch selected' };
    }
    try {
      const payload = {
        branch_id: branchId,
        name: data.name,
        description: data.description || null,
        ar: typeof data.ar === 'string' ? data.ar : JSON.stringify(data.ar || {}),
      };
      const { data: result, error } = await supabase.rpc('insert_dietary_preference' as any, {
        payload,
      });

      if (error) {
        throw error;
      }

      if (!result || result.success === false) {
        return {
          success: false,
          error: result?.message || 'Failed to add dietary preference',
        };
      }

      setHasDietaryChanges(true);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to add dietary preference' };
    }
  };

  const updateDietaryPreference = async (id: string, data: Pick<DietaryPreference, 'name' | 'description' | 'ar'>): Promise<{ success: boolean; error?: string }> => {
    try {
      const payload = {
        id: id,
        name: data.name,
        description: data.description || null,
        ar: typeof data.ar === 'string' ? data.ar : JSON.stringify(data.ar || {}),
      };

      const { data: result, error } = await supabase.rpc('edit_dietary_preference_by_id' as any, {
        payload: payload
      });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      };

      setHasDietaryChanges(true);
      return { success: true };
    } catch (error: any) {
      console.error('Error updating dietary preference:', error);
      return { success: false, error: error.message || 'Failed to update dietary preference' };
    }
  };

  const deleteDietaryPreference = async (id: string): Promise<{ success: boolean; error?: string }> => {
    try {
      const { data, error } = await supabase.rpc('delete_dietary_preference', {
        preference_id_text: id
      } as any);
      if (error) throw error;
      setHasDietaryChanges(true);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message || 'Failed to delete dietary preference' };
    }
  };

  useEffect(() => {
    fetchDietaryPreferences();
    fetchCurrencies();
  }, [branchId]);

  const clearDietaryChanges = () => {
    setHasDietaryChanges(false);
  };

  // Fetch currencies for the current branch using currencyService
  const fetchCurrencies = async () => {
    if (!branchId) {
      setCurrencies([]);
      setSelectedCurrencyState(null);
      lastFetchedBranchIdRef.current = null;
      return;
    }

    if (fetchingCurrenciesRef.current) return; // prevent duplicate calls
    if (lastFetchedBranchIdRef.current === branchId && currencies.length > 0) return; // reuse cached

    fetchingCurrenciesRef.current = true;
    setIsLoadingCurrencies(true);
    try {
      const list = await getCurrenciesByBranchId(branchId);
      setCurrencies(list);

      // Check localStorage for persisted selection per-branch
      const storedId = localStorage.getItem(`selectedCurrency:${branchId}`);
      const fromStorage = list.find((c) => c.id === storedId);
      if (fromStorage) {
        setSelectedCurrencyState(fromStorage);
      } else {
        // Default to base currency, else first available
        const base = list.find((c) => !!c.is_base);
        setSelectedCurrencyState(base || list[0] || null);
      }
      lastFetchedBranchIdRef.current = branchId;
    } catch (e) {
      setCurrencies([]);
      setSelectedCurrencyState(null);
    } finally {
      setIsLoadingCurrencies(false);
      fetchingCurrenciesRef.current = false;
    }
  };

  const refreshCurrencies = async () => {
    // force refetch by clearing cache marker
    lastFetchedBranchIdRef.current = null;
    await fetchCurrencies();
  };

  const setSelectedCurrency = (currencyId: string) => {
    const found = currencies.find((c) => c.id === currencyId) || null;
    setSelectedCurrencyState(found);
    if (branchId && found) {
      localStorage.setItem(`selectedCurrency:${branchId}`, found.id);
    }
  };


  return (
    <BranchContext.Provider
      value={{
        branchId,
        dietaryPreferences,
        isLoadingPreferences,
        hasDietaryChanges,
        refreshPreferences,
        addDietaryPreference,
        updateDietaryPreference,
        deleteDietaryPreference,
        clearDietaryChanges,
        // currencies
        currencies,
        selectedCurrency,
        isLoadingCurrencies,
        refreshCurrencies,
        setSelectedCurrency,
      }}
    >
      {children}
    </BranchContext.Provider>
  );
};

// Custom hook to use the branch context
export const useBranch = (): BranchContextType => {
  const context = useContext(BranchContext);

  if (context === undefined) {
    throw new Error('useBranch must be used within a BranchProvider');
  }

  return context;
};
